import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { label: "SERVICES", to: "/services", testid: "nav-services-link" },
  { label: "TRANSPORTATION OPTIONS", to: "/capacity", testid: "nav-capacity-link" },
  { label: "ABOUT", to: "/about", testid: "nav-about-link" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkClass = ({ isActive }) =>
    `relative px-1 py-2 text-[13px] font-semibold tracking-[0.14em] transition-colors duration-200 after:absolute after:left-0 after:bottom-0 after:h-[2px] after:bg-[#ff6a00] after:transition-all after:duration-300 ${
      isActive
        ? "text-[#191c1e] after:w-full"
        : "text-[#191c1e]/60 hover:text-[#191c1e] after:w-0 hover:after:w-full"
    }`;

  return (
    <>
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 bg-white/95 backdrop-blur-md transition-shadow duration-300 ${
        scrolled ? "shadow-[0_1px_0_0_#e5e7eb,0_8px_24px_-16px_rgba(25,28,30,0.25)]" : "border-b border-[#e5e7eb]"
      }`}
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:bg-[#191c1e] focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>
      <div className="container-30 flex h-[76px] items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" aria-label="30 NORTH — Home" className="flex shrink-0 items-center py-2">
          <img
            src="/assets/logo.png"
            alt="30 NORTH Transportation logo"
            className="h-9 w-auto sm:h-10"
            width="auto"
            height="40"
          />
        </Link>

        <div className="flex items-center gap-8 lg:gap-10">
          <nav aria-label="Primary" className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} data-testid={item.testid} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/request-a-quote"
              data-testid="nav-quote-button"
              className="hidden bg-[#ff6a00] px-6 py-3 text-[13px] font-bold tracking-[0.12em] text-white shadow-[0_10px_22px_-8px_rgba(255,106,0,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#e05d00] hover:shadow-[0_16px_30px_-8px_rgba(255,106,0,0.65)] active:translate-y-0 sm:inline-flex"
            >
              REQUEST A QUOTE
            </Link>
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-label={open ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="inline-flex h-11 w-11 items-center justify-center text-[#191c1e] lg:hidden"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

    </header>
    <div
      data-testid="mobile-nav-drawer"
      className={`fixed inset-x-0 top-[76px] bottom-0 z-40 overflow-y-auto bg-white transition-all duration-300 lg:hidden ${
        open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
      }`}
    >
      <nav aria-label="Mobile" className="container-30 flex min-h-full flex-col gap-2 py-8">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-${item.testid}`}
              className={({ isActive }) =>
                `flex items-center justify-between border-b border-[#e5e7eb] py-5 text-lg font-bold tracking-[0.1em] ${
                  isActive ? "text-[#ff6a00]" : "text-[#191c1e]"
                }`
              }
            >
              {item.label}
              <span className="h-2 w-2 bg-current opacity-40" aria-hidden="true" />
            </NavLink>
          ))}
          <Link
            to="/request-a-quote"
            data-testid="mobile-nav-quote-button"
            className="mt-8 bg-[#ff6a00] px-6 py-4 text-center text-sm font-bold tracking-[0.12em] text-white shadow-[0_10px_22px_-8px_rgba(255,106,0,0.55)] transition-all duration-200 hover:bg-[#e05d00]"
          >
            REQUEST A QUOTE
          </Link>
          <a
            href="tel:6138700294"
            data-testid="mobile-nav-phone-link"
            className="mt-3 border border-[#191c1e] px-6 py-4 text-center text-sm font-bold tracking-[0.12em] text-[#191c1e] transition-colors duration-200 hover:border-[#ff6a00] hover:text-[#ff6a00]"
          >
            CALL 613-870-0294
          </a>
        </nav>
      </div>
    </>
  );
};

export default Header;
